import { SpreedlyServiceConfig } from '../../types/ServiceConfigurations';
import {
  ISpreedlyService,
  SpreedlyEventHandlers,
  SpreedlyOnReadyFieldOptions,
  SpreedlyRequiredCardInfoContract,
} from '../../types/Spreedly';
import {
  SpreedlyValues,
  SupportedCreditCardTypes,
} from '../../utils/constants';

const SPREEDLY_SCRIPT_ID = 'spreedly-script';
class SpreedlyService implements ISpreedlyService {
  public isConfigured = false;

  private environmentKey = '';

  private cardNumberContainerId = '';

  private cvvContainerId = '';

  private scriptUrl = '';

  public configure(config: SpreedlyServiceConfig): void {
    this.environmentKey = config.environmentKey;
    this.cardNumberContainerId = config.cardNumberContainerId;
    this.cvvContainerId = config.cvvContainerId;
    this.scriptUrl = config.scriptUrl;
    this.isConfigured = true;
  }

  public loadScript(): void {
    const existingSpreedlyScript = document.getElementById(SPREEDLY_SCRIPT_ID);
    if (!existingSpreedlyScript) {
      const spreedlyScript = document.createElement('script');
      spreedlyScript.src = this.scriptUrl;
      spreedlyScript.id = SPREEDLY_SCRIPT_ID;
      document.body.appendChild(spreedlyScript);
    }
  }

  public initializeSpreedly(): void {
    if (this.isReady()) {
      Spreedly.removeHandlers();
      Spreedly.init(this.environmentKey, {
        numberEl: this.cardNumberContainerId,
        cvvEl: this.cvvContainerId,
      });
    }
  }

  public renderSpreedly(fieldOptions: SpreedlyOnReadyFieldOptions[]): void {
    if (this.isReady()) {
      Spreedly.on('ready', () => {
        fieldOptions.forEach(fieldOption => {
          const { field, placeHolder, fieldType, style, numberFormat } =
            fieldOption;
          if (placeHolder) Spreedly.setPlaceholder(field, placeHolder);
          Spreedly.setFieldType(field, fieldType);
          Spreedly.setStyle(field, style);
          if (numberFormat) {
            Spreedly.setNumberFormat(numberFormat);
          }
        });
      });
    }
  }

  public setupRecache(
    token: string,
    options: spreedly.SetRecacheOptions
  ): void {
    if (this.isReady()) {
      Spreedly.on('ready', () => {
        Spreedly.setRecache(token, options);
      });
    }
  }

  public setEventHandlers(eventHandlers: SpreedlyEventHandlers): void {
    if (this.isReady()) {
      const {
        onPaymentMethod,
        onValidation,
        onRecache,
        onErrors,
        onThreeDSStatus,
      } = eventHandlers;
      if (onValidation) Spreedly.on('validation', onValidation);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (onThreeDSStatus) Spreedly.on('3ds:status', onThreeDSStatus);
      if (onPaymentMethod) Spreedly.on('paymentMethod', onPaymentMethod);
      if (onRecache) Spreedly.on('recache', onRecache);
      if (onErrors) Spreedly.on('errors', onErrors);
    }
  }

  public validateCard(): void {
    if (this.isReady()) {
      Spreedly.validate();
    }
  }

  public tokenizeCard(
    requiredCardInfo: SpreedlyRequiredCardInfoContract
  ): void {
    const { cardHolderName, expMonth, expYear } = requiredCardInfo;
    if (this.isReady()) {
      Spreedly.tokenizeCreditCard({
        full_name: cardHolderName,
        month: expMonth,
        year: expYear,
      });
    }
  }

  public recacheCard(): void {
    if (this.isReady()) {
      Spreedly.recache();
    }
  }

  public static start3DSLifecycle(token: string, environmentKey: string): void {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const lifecycle = new Spreedly.ThreeDS.Lifecycle({
      environmentKey,
      hiddenIframeLocation: 'device-fingerprint',
      challengeIframeLocation: 'challenge',
      transactionToken: token,
      challengeIframeClasses: 'challenge-iframe',
    });

    lifecycle.start();
  }

  public static convertSpreedlyToV1CardType(spreedlyCardType: string): string {
    switch (spreedlyCardType) {
      case SpreedlyValues.CARD_TYPE_VISA:
        return SupportedCreditCardTypes.CARD_TYPE_VISA;
      case SpreedlyValues.CARD_TYPE_MASTER_CARD:
        return SupportedCreditCardTypes.CARD_TYPE_MASTER_CARD;
      case SpreedlyValues.CARD_TYPE_DISCOVER:
        return SupportedCreditCardTypes.CARD_TYPE_DISCOVER;
      case SpreedlyValues.CARD_TYPE_AMERICAN_EXPRESS:
        return SupportedCreditCardTypes.CARD_TYPE_AMERICAN_EXPRESS;
      case SpreedlyValues.CARD_TYPE_DINERS_CLUB_1:
      case SpreedlyValues.CARD_TYPE_DINERS_CLUB_2:
        return SupportedCreditCardTypes.CARD_TYPE_DINERS_CLUB;
      case SpreedlyValues.CARD_TYPE_JCB:
        return SupportedCreditCardTypes.CARD_TYPE_JCB;
      case SpreedlyValues.CARD_TYPE_ALELO:
        return SupportedCreditCardTypes.CARD_TYPE_ALELO;
      case SpreedlyValues.CARD_TYPE_ALIA:
        return SupportedCreditCardTypes.CARD_TYPE_ALIA;
      case SpreedlyValues.CARD_TYPE_CABAL:
        return SupportedCreditCardTypes.CARD_TYPE_CABAL;
      case SpreedlyValues.CARD_TYPE_CARNET:
        return SupportedCreditCardTypes.CARD_TYPE_CARNET;
      case SpreedlyValues.CARD_TYPE_CONFIABLE:
        return SupportedCreditCardTypes.CARD_TYPE_CONFIABLE;
      case SpreedlyValues.CARD_TYPE_DANKORT:
        return SupportedCreditCardTypes.CARD_TYPE_DANKORT;
      case SpreedlyValues.CARD_TYPE_ELO:
        return SupportedCreditCardTypes.CARD_TYPE_ELO;
      case SpreedlyValues.CARD_TYPE_MAESTRO:
        return SupportedCreditCardTypes.CARD_TYPE_MAESTRO;
      case SpreedlyValues.CARD_TYPE_NARANJA:
        return SupportedCreditCardTypes.CARD_TYPE_NARANJA;
      case SpreedlyValues.CARD_TYPE_OLIMPICA:
        return SupportedCreditCardTypes.CARD_TYPE_OLIMPICA;
      case SpreedlyValues.CARD_TYPE_SODEXO:
        return SupportedCreditCardTypes.CARD_TYPE_SODEXO;
      case SpreedlyValues.CARD_TYPE_VR:
        return SupportedCreditCardTypes.CARD_TYPE_VR;
      default:
        return spreedlyCardType;
    }
  }

  public static convertV1ToSpreedlyCardType(v1CardType: string): string {
    switch (v1CardType) {
      case SupportedCreditCardTypes.CARD_TYPE_VISA:
        return SpreedlyValues.CARD_TYPE_VISA;
      case SupportedCreditCardTypes.CARD_TYPE_MASTER_CARD:
        return SpreedlyValues.CARD_TYPE_MASTER_CARD;
      case SupportedCreditCardTypes.CARD_TYPE_DISCOVER:
        return SpreedlyValues.CARD_TYPE_DISCOVER;
      case SupportedCreditCardTypes.CARD_TYPE_AMERICAN_EXPRESS:
        return SpreedlyValues.CARD_TYPE_AMERICAN_EXPRESS;
      case SupportedCreditCardTypes.CARD_TYPE_DINERS_CLUB:
        return SpreedlyValues.CARD_TYPE_DINERS_CLUB_1;
      case SupportedCreditCardTypes.CARD_TYPE_JCB:
        return SpreedlyValues.CARD_TYPE_JCB;
      case SupportedCreditCardTypes.CARD_TYPE_ALELO:
        return SpreedlyValues.CARD_TYPE_ALELO;
      case SupportedCreditCardTypes.CARD_TYPE_ALIA:
        return SpreedlyValues.CARD_TYPE_ALIA;
      case SupportedCreditCardTypes.CARD_TYPE_CABAL:
        return SpreedlyValues.CARD_TYPE_CABAL;
      case SupportedCreditCardTypes.CARD_TYPE_CARNET:
        return SpreedlyValues.CARD_TYPE_CARNET;
      case SupportedCreditCardTypes.CARD_TYPE_CONFIABLE:
        return SpreedlyValues.CARD_TYPE_CONFIABLE;
      case SupportedCreditCardTypes.CARD_TYPE_DANKORT:
        return SpreedlyValues.CARD_TYPE_DANKORT;
      case SupportedCreditCardTypes.CARD_TYPE_ELO:
        return SpreedlyValues.CARD_TYPE_ELO;
      case SupportedCreditCardTypes.CARD_TYPE_MAESTRO:
        return SpreedlyValues.CARD_TYPE_MAESTRO;
      case SupportedCreditCardTypes.CARD_TYPE_NARANJA:
        return SpreedlyValues.CARD_TYPE_NARANJA;
      case SupportedCreditCardTypes.CARD_TYPE_OLIMPICA:
        return SpreedlyValues.CARD_TYPE_OLIMPICA;
      case SupportedCreditCardTypes.CARD_TYPE_SODEXO:
        return SpreedlyValues.CARD_TYPE_SODEXO;
      case SupportedCreditCardTypes.CARD_TYPE_VR:
        return SpreedlyValues.CARD_TYPE_VR;
      default:
        return v1CardType;
    }
  }

  private isReady(): boolean {
    return Boolean(Spreedly && this.isConfigured);
  }
}

export default SpreedlyService;
